<template>
  <div class="weapon_wrap">
    <div class="weapon-info container">
      <div class="item-image">
        <Thumb
          :alt="`${data.localed_name} ${isRare}` ? t('rare_skins') : t('Skins') "
          :font-size="25"
          :height="25"
          :image-height="250"
          :image-width="350"
          :item="weapon as Item"
          :size="25"
          :src="getImage(weapon.full_name || '')"
          :use-proxy="false"
          image-sizes="500:500"
          loading="eager"
        />
      </div>
      <div class="item-info">
        <div class="item-info__header">
          <h1 class="item-name">
            <span>{{ data.localed_name }} {{ isRare ? t('rare_skins') : t('Skins') }}</span>
          </h1>
        </div>
        <span class="item-description">
          {{
            cleanText(data.description) || t(`weapon_page_title`, { name: weapon?.weapon })
          }}
        </span>
      </div>
    </div>

    <!--        :class="currW > 1000 && data.result.length <= 7 ? 'container' : ''" -->
    <div
      v-if="data.result?.length"
      :class="{ container: data.result.length <= 7, p0: currW < 1000 }"
      class="pb60 f fs ft gap32 wrapper weapon-items"
    >
      <UiItemsGrid>
        <LazyPagesContainerMulticard
          v-if="data.knives?.length || data.gloves?.length"
          :category="!isRare ? data.knives?.length ? 'Knives' : 'Gloves' : 'Normal'"
          :is-rare="isRare"
          :items="(!isRare ? (data.knives?.length ? data.knives : data.gloves) : data.normal) as string[]"
        />

        <LazyPagesWeaponComponentsSkinCard
          v-for="item in data.result"
          :key="item?.market_hash_name"
          :item="item as ItemsByField"
          :link="getItemLinkWithoutExt({
            name: getName(item?.market_hash_name || ''),
            slug: item?.link,
          })"
        />
      </UiItemsGrid>
      <LazyUiNoResult
        v-if="!pending && !data.result?.length"
        class="mxa"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Item, ItemsByField, ItemsContainerPageResult } from '~/graphql/generated/schema-types';
import Thumb from '~/components/Pages/Item/Components/Children/Thumb.vue';
import { useResize } from '~/composables/resize';
import { getImageFromWaxpeer } from '~/utils/get-image-url';
import { getItemLinkWithoutExt } from '~/utils/name-utils';

defineProps<{
  data: ItemsContainerPageResult
  pending: boolean
  isRare: boolean
}>();

const route = useRoute();
const { t } = useI18n();
const currW = useResize();

const weapon = computed(() => {
  return {
    full_name: route.params.id as string,
    weapon: route.params.id as string,
  } as Item;
});

function getName(itemName: string) {
  if (!itemName.includes('Sticker')) {
    return itemName?.replace(/^\s*|\s*\([^)]*\)\s*|\s*$/g, '');
  }
  return itemName;
}

function getImage(name: string) {
  // name = getModifiedString(name)
  //
  // if (name.includes('case') && !route.params.id.includes('case')) {
  //   return `/images/cases/${name}.webp`
  // }
  return getImageFromWaxpeer(name || '', '500:500');
}

function cleanText(html: string) {
  if (!html)
    return html || '';
  // Удаление HTML-тегов
  let result = html.replace(/<[^>]+(>|$)/g, '');
  // Удаление специфических символов и последовательностей, таких как \n и \
  result = result.replace(/\\n/g, ' ').replace(/\\/g, '');
  // Удаление множественных пробелов, табуляций и переносов строк
  return result.replace(/\s+/g, ' ').trim();
}
</script>

<style lang="scss" scoped>
.weapon {
  &_wrap {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 7rem;

    @media screen and (max-width: 1250px) {
      margin-top: 0;
    }
  }

  &-info {
    width: 100%;
    display: flex;
    gap: 4.8rem;
    color: var(--surfaces-on-surface);

    .item-image {
      width: 38.4rem;
      height: 38.4rem;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      @media screen and (max-width: 525px) {
        width: 100%;
        min-width: 100%;
        margin-top: 10px;
      }
    }

    .item-info {
      display: flex;
      flex-direction: column;
      gap: 20px;

      &__header {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .item-name {
        font-weight: 600;
        font-size: 2.8rem;
        line-height: 3.8rem;
      }

      .item-description {
        font-size: 1.6rem;
        opacity: 0.8;
        max-width: 68rem;
      }
    }
  }
}

@media screen and (max-width: 1075px) {
  .weapon {
    .weapon_wrap {
      gap: 4rem;
    }

    &-info {
      .item-name {
        font-size: 2.4rem;
        line-height: 3.4rem;
      }

      .item-description {
        font-size: 1.4rem;
        line-height: 3rem;
      }
    }
  }
}

@media screen and (max-width: 979px) {
  .weapon-items {
    padding-right: 16px;
    padding-left: 16px;
  }
}

@media screen and (max-width: 850px) {
  .weapon-info {
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .item-info {
      align-items: center;
      width: 100%;

      .item-tags {
        flex-wrap: nowrap;
        justify-content: center;
        overflow-x: auto;
        width: 100%;
        padding-bottom: 10px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .weapon {
    .weapon_wrap {
      gap: 4rem;
    }

    .fg {
      width: 100%;
    }

    .wrapper {
      flex-direction: column;
    }

    &-info {
      gap: 0;

      .item-info {
        margin-left: 0;
        margin-top: 1.6rem;

        .item-name {
          font-size: 2.2rem;
          line-height: 3.2rem;
        }

        .item-description {
          margin-top: 1.2rem;
          line-height: 2.8rem;
        }
      }
    }
  }
}

@media screen and (max-width: 525px) {
  .weapon {
    .weapon_wrap {
      gap: 1.6rem;
    }

    &-info {
      .item-image {
        margin-left: 1.5rem;
        margin-right: 1.5rem;
        width: 34.5rem;
        background: rgba(33, 40, 53, 0.2);
        border-radius: 1.2rem;
        max-width: 29rem;
      }

      .item-info {
        .item-name {
          font-size: 2rem;
          line-height: 2.7rem;
        }

        .item-description {
          margin-top: 0.8rem;
          line-height: 2.6rem;
        }
      }
    }
  }
}
</style>
